import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import { FormikMultiTextField } from 'components/FormikMultiTextField/FormikMultiTextField';
import { FormikSelectField } from 'components/FormikSelectField/FormikSelectField';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { AdvancedFilter } from 'components/Table/components/AdvancedFilter/AdvancedFilter';
import {
  type GroupFilter,
  useAdvancedFilter,
} from 'components/Table/components/AdvancedFilter/useAdvancedFilter';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, FormikProvider, useFormik } from 'formik';
import { memo } from 'react';
import type { FC } from 'react';
import { FormikRateLimitingSettingsFields } from './fields/FormikRateLimitingSettingsFields';
import { FormikVendorApiBaseUrlField } from './fields/FormikVendorApiBaseUrlField';
import { FormikVendorApiCommentField } from './fields/FormikVendorApiCommentField';
import { FormikVendorApiEnabledField } from './fields/FormikVendorApiEnabledField';
import { FormikVendorApiRespectSystemProxyConfigurationField } from './fields/FormikVendorApiRejectUnauthorizedField';
import { FormikVendorApiRejectUnauthorizedField } from './fields/FormikVendorApiRespectSystemProxyConfigurationField';
import { FormikVendorApiSlugField } from './fields/FormikVendorApiSlugField';
import type { BaseVendorFormValues, VendorDialogProps } from './vendorTypes';

import type {
  AdvancedFilterTableColumnDefinition,
  MerakiFilterGroup,
  settingTypes,
} from '@ipfabric/utils';
import { validateSlug } from 'utils/formValidation/validations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { VendorDialogContainer } from './VendorDialogContainer';
import { FormikAdvancedSettings } from './fields/FormikAdvancedSettings';
import { getAdvancedSettingsDefaultValues } from './helpers/applyDefaultInitialValues';
import { isVendorUpdate } from './helpers/guardTypes';

export type MerakiFormValues =
  BaseVendorFormValues<settingTypes.MerakiApiSettings>;

type Props = VendorDialogProps<
  MerakiFormValues,
  settingTypes.MerakiApiSettings,
  settingTypes.UpdateMerakiApiSettings
>;

const merakiFilterColumns: AdvancedFilterTableColumnDefinition<unknown>[] = [
  {
    key: 'platform',
    title: 'Platform',
    filter: { type: 'text', operators: ['eq', 'neq', 'reg', 'nreg'] },
  },
  {
    key: 'tag',
    title: 'Tag',
    filter: { type: 'text', operators: ['eq', 'neq', 'reg', 'nreg'] },
  },
  {
    key: 'networkId',
    title: 'Network ID',
    filter: { type: 'text', operators: ['eq', 'neq', 'reg', 'nreg'] },
  },
];

const emptyMerakiFilterGroup: MerakiFilterGroup = {
  type: 'groupFilter',
  operator: 'and',
  filters: [],
};

const resetSecretAttributes = (values: MerakiFormValues): MerakiFormValues => {
  const { merakiFilters } = values;
  // Guard against persisted data in a legacy/incompatible format (e.g. the old
  // flat MerakiDeviceFilter[] array). The nullish coalescing alone is not
  // sufficient because an array is truthy; explicitly validate the shape.
  const safeFilters =
    merakiFilters?.type === 'groupFilter' &&
    Array.isArray(merakiFilters.filters)
      ? merakiFilters
      : emptyMerakiFilterGroup;
  return {
    ...values,
    apiKey: '',
    merakiFilters: safeFilters,
  };
};

const apiVerOptions = [{ label: 'v1', value: 'v1' }];

const BareMerakiForm: FC<Props> = (props) => {
  const msg = useLocale();
  const editMode = props.dialogType === 'edit';
  const {
    discoverySettings: {
      vendorsApi: { merakiForm: merakiFormMsg },
    },
  } = useLocale();
  const formik = useFormik({
    initialValues: resetSecretAttributes(props.initialValues),
    validate: (values) => {
      const { apiKey, baseUrl } = values;
      const errors: FormikErrors<MerakiFormValues> = {};
      if (!editMode && !apiKey) {
        errors.apiKey = merakiFormMsg.validation.required;
      }
      if (!baseUrl) {
        errors.baseUrl = merakiFormMsg.validation.required;
      } else if (!baseUrl.startsWith('https://')) {
        errors.baseUrl = merakiFormMsg.validation.httpsRequired;
      }
      validateSlug(values, errors, msg);
      return errors;
    },
    onSubmit: makeFormikOnSubmit((values) => {
      const formattedValues = {
        ...values,
        ...getAdvancedSettingsDefaultValues(values),
      };

      if (isVendorUpdate(values) && props.dialogType === 'edit') {
        props.onSubmit({
          ...formattedValues,
          id: values.id,
          apiKey: values.apiKey || props.initialValues.apiKey || undefined,
        });
      } else if (props.dialogType === 'add') {
        props.onSubmit(formattedValues);
      }
    }),
  });

  const advancedFilterHook = useAdvancedFilter({
    // MerakiFilterGroup is structurally compatible with GroupFilter at runtime;
    // the cast bridges the two structural aliases.
    value: formik.values.merakiFilters as GroupFilter | undefined,
    onChange: (value) => formik.setFieldValue('merakiFilters', value),
  });

  return (
    <FormikProvider value={formik}>
      <VendorDialogContainer
        formik={formik}
        dialogType={props.dialogType}
        {...props.vendorDialogContainerPassthruProps}
      >
        <FormikVendorApiEnabledField formik={formik} />
        <FormikTextField
          formik={formik}
          label={merakiFormMsg.apiKey}
          placeholder={editMode ? '(unchanged)' : undefined}
          name='apiKey'
          type='text'
        />
        <FormikMultiTextField
          formik={formik}
          label={
            <>
              {merakiFormMsg.organizations}
              <TooltipIcon
                content={merakiFormMsg.organizationsHelp}
                iconProps={{
                  iconStyle: 'solid',
                  inline: true,
                }}
                className='ml-2'
                size='sm'
              />
            </>
          }
          name='organizations'
        />
        <fieldset className='border border-gray-300 rounded py-4 px-0 mt-4 mb-2'>
          <legend className='text-sm font-semibold px-1'>
            Filtering options
          </legend>
          <p className='text-xs text-gray-500 mb-3'>
            Limit discovery to specific devices. Use the Platform, Tag, or
            Network ID columns to build filter rules. Rules support exact match
            and regex operators.
          </p>
          <AdvancedFilter
            advancedFilterHook={advancedFilterHook}
            columns={merakiFilterColumns}
            onFilterChange={() => {}}
            onSubmit={() => {}}
            submitCount={0}
          />
        </fieldset>
        <FormikSelectField
          formik={formik}
          label={merakiFormMsg.apiVer}
          name='apiVer'
          nonClearable
          options={apiVerOptions}
        />
        <FormikVendorApiBaseUrlField formik={formik} />
        <FormikVendorApiSlugField formik={formik} />
        <FormikVendorApiCommentField formik={formik} />
        <FormikVendorApiRespectSystemProxyConfigurationField formik={formik} />
        <FormikVendorApiRejectUnauthorizedField formik={formik} />
        <FormikAdvancedSettings>
          <FormikRateLimitingSettingsFields formik={formik} />
        </FormikAdvancedSettings>
      </VendorDialogContainer>
    </FormikProvider>
  );
};

export const MerakiForm = memo(BareMerakiForm);
